import { pipe, map, prop } from 'ramda';

import { multiply, divide, sum, toDecimalPlaces } from 'utils/calculator';
import { PRIMARY_LOYALTY_ACCOUNT_ADDITIONAL_DETAILS_KEY } from 'constants/booking';
import { codes as paymentCodes } from 'constants/payment';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import { ERROR_MODAL_TYPE, REFUND_100 } from 'constants/prrRefund';

export const LOYALTY_TAB_VALUE = 'loyalty';
export const LOYALTY_TAB_LABEL = 'Loyalty points';
export const CARD_TAB_VALUE = 'card';
export const CARD_TAB_LABEL = 'Card';
export const BAF_TAB_VALUE = 'baf';
export const BAF_TAB_LABEL = 'Business Account';
export const VOUCHER_TAB_VALUE = 'voucher';
export const VOUCHER_TAB_LABEL = 'Voucher';

export const loyaltyTab = {
  value: LOYALTY_TAB_VALUE,
  label: LOYALTY_TAB_LABEL
};

export const cardTab = {
  value: CARD_TAB_VALUE,
  label: CARD_TAB_LABEL
};
export const voucherTab = {
  value: VOUCHER_TAB_VALUE,
  label: VOUCHER_TAB_LABEL
};

export const bafTab = {
  value: BAF_TAB_VALUE,
  label: BAF_TAB_LABEL
};

export const getTotalPrice = pipe(map(prop('price')), sum);
export const getTotalPoints = pipe(map(prop('loyaltyPoints')), sum);

export const getRefundAmount = (totalPrice, refundPercentage) =>
  toDecimalPlaces(2, multiply(divide(totalPrice, 100), Number(refundPercentage)));

const loyaltyTicketRefundAmountMap = {
  GBP: {
    30: {
      B07: 17,
      BD0: 17,
      B01: 17,
      TB00: 17,
      W00: 17,
      C00: 17,
      TW00: 17,
      TC00: 17,
      H08: 34,
      HD0: 34,
      H11: 34,
      H01: 34,
      TH00: 34,
      A01: 47,
      TA00: 47
    },
    60: {
      B07: 34,
      BD0: 34,
      B01: 34,
      TB00: 34,
      W00: 34,
      C00: 34,
      TW00: 34,
      TC00: 34,
      H08: 67.5,
      HD0: 67.5,
      H11: 67.5,
      H01: 67.5,
      TH00: 67.5,
      A01: 94,
      TA00: 94
    },
    75: {
      B07: 50.5,
      BD0: 50.5,
      B01: 50.5,
      TB00: 50.5,
      W00: 50.5,
      C00: 50.5,
      TW00: 50.5,
      TC00: 50.5,
      H08: 101.5,
      HD0: 101.5,
      H11: 101.5,
      H01: 101.5,
      TH00: 101.5,
      A01: 141,
      TA00: 141
    }
  },
  EUR: {
    30: {
      B07: 22.5,
      BD0: 22.5,
      B01: 22.5,
      TB00: 22.5,
      W00: 22.5,
      C00: 22.5,
      TW00: 22.5,
      TC00: 22.5,
      H08: 45,
      HD0: 45,
      H11: 45,
      H01: 45,
      TH00: 45,
      A01: 63,
      TA00: 63
    },
    60: {
      B07: 45,
      BD0: 45,
      B01: 45,
      TB00: 45,
      W00: 45,
      C00: 45,
      TW00: 45,
      TC00: 45,
      H08: 90,
      HD0: 90,
      H11: 90,
      H01: 90,
      TH00: 90,
      A01: 125,
      TA00: 125
    },
    75: {
      B07: 67.5,
      BD0: 67.5,
      B01: 67.5,
      TB00: 67.5,
      W00: 67.5,
      C00: 67.5,
      TW00: 67.5,
      TC00: 67.5,
      H08: 135,
      HD0: 135,
      H11: 135,
      H01: 135,
      TH00: 135,
      A01: 188,
      TA00: 188
    }
  }
};

export const getLoyaltyTicketRefundAmount = (bucketCode, refundPercentage, currency) => {
  if (!['EUR', 'GBP'].includes(currency)) {
    return 0;
  }

  return loyaltyTicketRefundAmountMap[currency][refundPercentage][bucketCode];
};

export const getLoyaltyTicketsRefundAmount = (products, refundPercentage) =>
  products.reduce((acc, product) => acc + getLoyaltyTicketRefundAmount(product.bucketCode, refundPercentage, product.currency), 0);

export const prrRefundablePayments = (payments = []) => {
  const prrRefundMethods = [
    paymentCodes.CARD,
    paymentCodes.APPLE_PAY,
    paymentCodes.GOOGLE_PAY,
    paymentCodes.KLARNA_BNPL,
    paymentCodes.IDEAL,
    paymentCodes.PAYPAL,
    paymentCodes.BUSINESS_ACCOUNT
  ];

  return payments.filter((p) => prrRefundMethods.includes(p.method));
};

const onRefundCard =
  (processCardPrrRefund) =>
  async ({ amount, pspReference, currency, hasHotel, bookingReference, paymentLocation, additionalDetails, compensationRecordsToUpdate }) =>
    processCardPrrRefund({
      variables: {
        currency,
        pspReference,
        bookingReference,
        refundAmount: amount,
        paymentLocation: paymentLocation.name,
        additionalDetails,
        hasHotel,
        compensationRecordsToUpdate
      }
    });

export const getRefundHandlers = ({ processCardPrrRefund }) => ({
  onRefundCard: onRefundCard(processCardPrrRefund),
  onRefundApplePay: onRefundCard(processCardPrrRefund),
  onRefundGooglePay: onRefundCard(processCardPrrRefund),
  onRefundKlarna: onRefundCard(processCardPrrRefund)
});

export const getDisplayForPayment = (payment) => {
  if (payment.method === paymentCodes.PAYPAL || payment.method === paymentCodes.IDEAL) {
    return `${payment.reference} - ${payment.method}`;
  }

  if (payment.method === paymentCodes.KLARNA_BNPL) {
    return payment.method.split('_')[0];
  }

  return `...${payment.externalReference}`;
};

export const getMembershipNumberInBooking = (additionalDetails = []) => {
  const membershipNumberAssociatedToBooking = additionalDetails?.find((ad) => ad.key === PRIMARY_LOYALTY_ACCOUNT_ADDITIONAL_DETAILS_KEY);

  return membershipNumberAssociatedToBooking ? membershipNumberAssociatedToBooking.value : null;
};

export const isValidMembershipNumber = (membershipNumberInBooking = null, membershipNumberInURL = null) => {
  if (!membershipNumberInBooking) {
    return !!membershipNumberInURL;
  }
  if (membershipNumberInBooking) {
    return membershipNumberInURL ? membershipNumberInBooking === membershipNumberInURL : true;
  }

  return true;
};

export const isThroughFareWithSN = ({ serviceTypes, service = serviceTypeCodes.EUROSTAR }) =>
  serviceTypes.length > 1 && serviceTypes.every((serviceType) => serviceType === service || serviceType === serviceTypeCodes.SNCF);

export const getRefundOptions = (
  compensationItems = [],
  serviceTypes = [],
  defaultOptions = [],
  isVoucher = false,
  isLoyalty = false,
  isCashTicket = false
) => {
  const options = [];

  if (compensationItems.length > 0) {
    const compensationPercent =
      compensationItems[0][
        isVoucher && !isCashTicket
          ? 'voucherCompensationPercentage'
          : isLoyalty && !isCashTicket
          ? 'loyaltyCompensationPercentage'
          : 'cashCompensationPercentage'
      ];
    options.push({
      value: compensationPercent,
      label: `${compensationPercent}% - ${compensationItems[0].delayDurationMins}`,
      isExecutiveDecision: compensationPercent > 75
    });
  } else {
    options.push(...defaultOptions);
  }

  if (
    !isLoyalty &&
    compensationItems.length === 0 &&
    serviceTypes.length > 0 &&
    serviceTypes.every((type) => ['SN', 'ER'].includes(type))
  ) {
    options.push(REFUND_100);
  }

  return options;
};

export const getCompensationErrorStatus = (refundType = ERROR_MODAL_TYPE.NONE, compensationItems = [], updatedCompensationRecords = []) => {
  let modalTitle;
  const updatedRecords = [];
  const manuallyUpdateRecords = [];

  switch (refundType) {
    case ERROR_MODAL_TYPE.CARD:
      modalTitle = 'prrRefund.cardCompensationErrorTitle';
      break;
    case ERROR_MODAL_TYPE.VOUCHER:
      modalTitle = 'prrRefund.voucherCompensationErrorTitle';
      break;
    case ERROR_MODAL_TYPE.BUSINESS_ACCOUNT:
      modalTitle = 'prrRefund.businessAccountCompensationErrorTitle';
      break;
    case ERROR_MODAL_TYPE.LOYALTY:
      modalTitle = 'prrRefund.loyaltyCompensationErrorTitle';
      break;
    default:
      modalTitle = '';
  }

  compensationItems.forEach(({ compensationRecordId, ticketNumber }) => {
    if (updatedCompensationRecords.includes(compensationRecordId)) {
      updatedRecords.push(ticketNumber);
    } else {
      manuallyUpdateRecords.push(ticketNumber);
    }
  });

  if (!compensationItems.length || !manuallyUpdateRecords.length) {
    return null;
  }

  return {
    refundType,
    modalTitle,
    updatedRecords,
    manuallyUpdateRecords
  };
};
